
import { Vue, Component, Emit, Prop, Watch } from "vue-property-decorator";
import { validationMixin } from "vuelidate";
import vSelect from "vue-select";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { Getter } from "vuex-class";
import { UserPermissions } from "@/store/types";
import {AdvancedStatusFilter} from "@/types/sv-data/system-view/advanced-filters/AdvancedStatusFilter";
import { maxLength, minLength, required } from "vuelidate/lib/validators";
import { AreaNode } from "@/types/sv-data/groups/AreaNode";
import ApiSystemViewAdvancedFiltersService from "@/services/api.systemViewAdvancedFilters.service";
import { get } from "lodash";
import DeviceNameAdvancedFilter from "@/components/system-view/advanced-filters/DeviceNameAdvancedFilter.vue";
import DeviceTypeAdvancedFilter from "@/components/system-view/advanced-filters/DeviceTypeAdvancedFilter.vue";
import LastTimeAlarmAdvancedFilter from "@/components/system-view/advanced-filters/LastTimeAlarmAdvancedFilter.vue";
import PeriodAlarmCountAdvancedFilter
	from "@/components/system-view/advanced-filters/PeriodAlarmCountAdvancedFilter.vue";
import { AdvancedStatusFilterRuleBase,
	DeviceNameAdvancedFilterRule,
	DeviceTypeAdvancedFilterRule,
	LastTimeAlarmAdvancedFilterRule,
	PeriodAlarmCountAdvancedFilterRule }
	from "@/types/sv-data/system-view/advanced-filters/AdvancedStatusFilterRules";

const removeWhiteSpace = (value: string) => {
	return value.toLowerCase().replace(/\s/g, "");
};

@Component({
	mixins: [validationMixin],
	validations() {
		return {
			newFilter: {
				name: {
					required,
					minLength: minLength(1),
					maxLength: maxLength(250),
					validateDuplicate: formTitle => {
						const title: undefined | string = get(this.newFilter, "name");
						if(this.filter?.name == title) {
							return true;
						}
						return (this.allAvailableFilters
							.every(({ name }) => name !== formTitle));
					}
				}
			}
		};
	},
	components: {
		PeriodAlarmCountAdvancedFilter,
		LastTimeAlarmAdvancedFilter,
		DeviceTypeAdvancedFilter,
		DeviceNameAdvancedFilter,
		"vue-select": vSelect,
		"area-tree-select": AreaTreeSelect,
		VuePerfectScrollbar
	}
})
export default class SystemViewAdvancedFilterEdit extends Vue {
	@Prop(Boolean) shown: boolean;
	@Prop(Object) filter: AdvancedStatusFilter;
	@Prop({ type: Array, default: () => [] }) allAvailableFilters: AdvancedStatusFilter[];

	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getUserId") private userId: number;

	private isLoading: boolean = false;
	private areaList: AreaNode[] = [];
	private showErrorAnimation: boolean = false;
	private newFilter: AdvancedStatusFilter = null;
	private rulesCollection: any[] = [];
	private emptyAreaAfterInput: boolean = false;

	public async onSubmit(): Promise<void> {
		if (this.isLoading) {
			return;
		}

		this.newFilter.areas = this.areaList;
		this.newFilter.rules = JSON.stringify(this.rulesCollection);

		try {
			let result;
			if (this.newFilter.advancedStatusFilterId !== null) {
				// Update
				result = await ApiSystemViewAdvancedFiltersService.updateAdvancedStatusFilter(this.newFilter);
			} else {
				// Create
				result = await ApiSystemViewAdvancedFiltersService.createAdvancedStatusFilter(this.newFilter);
			}
			if(result != null) {
				this.$emit("submit");
				await this.resetNewFilter();
			}
		} catch (exception) {
			this.$notify({
				type: "error",
				title: "Error",
				text: exception.message
			});
		}
	}

	@Emit("cancel")
	public onCancel() {
		return;
	}

	@Watch("shown")
	private async onShow(): Promise<void> {
		if (!this.shown) {
			return;
		}

		this.emptyAreaAfterInput = false;

		if (this.filter !== null) {
			try {
				this.newFilter = Object.assign({}, this.filter);
				this.rulesCollection = JSON.parse(this.filter.rules);
				this.areaList = [];
				this.filter.areas.forEach((value) => {
					this.areaList.push({
						id: value.id,
						label: value.label
					});
				});
			}
			catch(exception) {

				this.$notify({
					type: "error",
					title: "Error",
					text: "Failed to get advanced filter information"
				});
				console.error(exception.message);

				await this.resetNewFilter();
			}
		} else {
			await this.resetNewFilter();
		}
	}

	private async mounted(): Promise<void> {
		this.$v.$reset();
		await this.resetNewFilter();
	}

	private areaSelected(selectedArea: AreaNode): void {
		if (!selectedArea) {
			return;
		}
		let groupIndex = this.areaList.findIndex((area: any) => area.id == selectedArea.id);

		if (groupIndex < 0 || groupIndex === null || groupIndex === undefined) {
			this.areaList.push({
				id: selectedArea.id,
				label: selectedArea.label
			});
		} else {
			this.areaList.splice(groupIndex, 1);
		}
		this.emptyAreaAfterInput = this.areaList.length == 0;
	}

	private async resetNewFilter(): Promise<void> {
		this.newFilter = {
			advancedStatusFilterId: null,
			name: "",
			userId: this.userId,
			areas: [],
			shared: false,
			combineFilters: false,
			rules: ""
		};
		this.areaList = [];
		this.rulesCollection = [];
	}

	private get getDeviceNameRule(): DeviceNameAdvancedFilterRule {
		let rule = this.rulesCollection.filter(rules => rules.filterRuleType == DeviceNameAdvancedFilter.RuleIdentifier);
		if(rule !== null && rule.length > 0) {
			return rule[0] as DeviceNameAdvancedFilterRule;
		}
		return null;
	}

	private get getDeviceTypeRule(): DeviceTypeAdvancedFilterRule {
		let rule = this.rulesCollection.filter(rules => rules.filterRuleType == DeviceTypeAdvancedFilter.RuleIdentifier);
		if(rule !== null && rule.length > 0) {
			return rule[0] as DeviceTypeAdvancedFilterRule;
		}
		return null;
	}

	private get getLastTimeAlarmRule(): LastTimeAlarmAdvancedFilterRule {
		let rule = this.rulesCollection.filter(rules => rules.filterRuleType == LastTimeAlarmAdvancedFilter.RuleIdentifier);
		if(rule !== null && rule.length > 0) {
			return rule[0] as LastTimeAlarmAdvancedFilterRule;
		}
		return null;
	}

	private get getPeriodAlarmCountRule(): PeriodAlarmCountAdvancedFilterRule {
		let rule = this.rulesCollection.filter(rules => rules.filterRuleType == PeriodAlarmCountAdvancedFilter.RuleIdentifier);
		if(rule !== null && rule.length > 0) {
			return rule[0] as PeriodAlarmCountAdvancedFilterRule;
		}
		return null;
	}

	private async updateFilterRule(filterRule: AdvancedStatusFilterRuleBase): Promise<void> {
		let existing = this.rulesCollection.findIndex((rule: AdvancedStatusFilterRuleBase) => rule.filterRuleType == filterRule.filterRuleType);
		if(existing !== -1) {
			this.rulesCollection.splice(existing, 1);
		}
		this.rulesCollection.push(filterRule);
	}

	private get submitButtonTitle(): string {
		return this.filter ? "Save" : "Add";
	}

	private get disableSubmit(): boolean {
		return this.isLoading || this.$v.$invalid || this.areaList?.length === 0;
	}
}
